import produce from "immer"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { useHistory } from "react-router"

import { useTranslation } from "react-i18next"
import { useBrandName, usePageTracking } from "../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../models"
import type { ProjectComparisonDto, ProjectDto } from "../../../models"
import {
  bosatlasComparisonDelete,
  bosatlasComparisonUpdate,
  bosatlasComparisonUpdateBundle,
  bosatlasCreateBundle,
  bosatlasProjectDelete,
  bosatlasProjectUpdate,
  bosatlasProjectUpdateBundle,
  bosatlasSearch,
  bosatlasUpdateBundle,
  bundleDtoToBundle,
  fetchBosatlas
} from "../../../redux"
import type { AppState, AppThunkDispatch, BosatlasActions, Bundle } from "../../../redux"
import { PageContainer } from "../../molecules"
import type { ISelectBundleDialogProps, ITextInputDialogProps } from "../../molecules"
import { BosatlasViewer } from "../../organisms"

export interface IBosatlasPageStateProps {
  bundles?: Bundle[]
  projects?: ProjectDto[]
  comparisons?: ProjectComparisonDto[]
  sharedProjects?: ProjectDto[]
  sharedComparisons?: ProjectComparisonDto[]
  fetchBosatlas: () => void
  removeComparison: (comparison: ProjectComparisonDto) => void
  removeProject: (project: ProjectDto) => void
  updateComparison: (comparison: ProjectComparisonDto) => void
  updateComparisonBundles: (comparison: ProjectComparisonDto, bundles: Bundle[], callback?: () => void) => void
  updateProject: (project: ProjectDto) => void
  updateProjectBundles: (project: ProjectDto, bundles: Bundle[], callback?: () => void) => void
  createBundle: (name: string) => void
  updateBundle: (id: string, name: string) => void
}

function BosatlasPageView(props: IBosatlasPageStateProps): ReactElement {
  const {
    bundles,
    projects,
    comparisons,
    sharedProjects,
    sharedComparisons,
    fetchBosatlas,
    removeComparison,
    removeProject,
    updateComparison,
    updateComparisonBundles,
    updateProject,
    updateProjectBundles,
    createBundle,
    updateBundle
  } = props

  const { t } = useTranslation("myBosatlas")

  const [textInputDialogState, setTextInputDialogState] = useState<ITextInputDialogProps>({
    open: false,
    title: "",
    label: "",
    onCancel: () => {},
    onClose: () => {},
    onConfirm: () => {}
  })
  const [selectBundleDialogState, setSelectBundleDialogState] = useState<ISelectBundleDialogProps>({
    open: false,
    bundles: [],
    onClose: () => {},
    onSelect: () => {}
  })

  const history = useHistory()
  const brandName: string = useBrandName()
  usePageTracking(AnalyticsPageCategory.ContentLibrary, AnalyticsPageType.Project)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchBosatlas()
  }, [])

  function startBundleEdit(bundle?: Bundle): void {
    const editing: boolean = bundle !== undefined

    setTextInputDialogState({
      open: true,
      title: editing ? t("cards.changeFolderName") : t("cards.createNewFolder"),
      defaultValue: editing ? bundle?.title : "",
      placeholder: t("cards.folderName"),
      confirmLabel: editing ? t("cards.changeFolder") : t("cards.createFolder"),
      onCancel: closeTextInputDialog,
      onClose: closeTextInputDialog,
      onConfirm: (value: string) => {
        closeTextInputDialog()
        updateCreateBundle(value, bundle)
      }
    })
  }

  function onProjectClick(project: ProjectDto): void {
    history.push(`/project/${project.id}`)
  }

  function onComparisonClick(comparison: ProjectComparisonDto): void {
    history.push(`/comparison/${comparison.id}`)
  }

  function onBundleClick(bundleId: string): void {
    history.push(`Mijn${brandName}/${bundleId}`)
  }

  function handleComparisonUpdateRequest(comparison: ProjectComparisonDto): void {
    setTextInputDialogState({
      open: true,
      title: t("cards.changeName"),
      label: t("cards.name"),
      defaultValue: comparison.title,
      onCancel: closeTextInputDialog,
      onClose: closeTextInputDialog,
      onConfirm: (value: string) => {
        closeTextInputDialog()
        updateComparisonTitle(comparison, value)
      }
    })
  }

  function updateComparisonTitle(comparison: ProjectComparisonDto, title: string): void {
    const newComparison: ProjectComparisonDto = produce(comparison, (draft: ProjectComparisonDto) => {
      draft.title = title
    })
    updateComparison(newComparison)
  }

  function handleProjectUpdateRequest(project: ProjectDto): void {
    setTextInputDialogState({
      open: true,
      title: t("cards.changeName"),
      label: t("cards.name"),
      defaultValue: project.title,
      onCancel: closeTextInputDialog,
      onClose: closeTextInputDialog,
      onConfirm: (value: string) => {
        closeTextInputDialog()
        updateProjectTitle(project, value)
      }
    })
  }

  function updateProjectTitle(project: ProjectDto, title: string): void {
    const newProject: ProjectDto = produce(project, (draft: ProjectDto) => {
      draft.title = title
    })
    updateProject(newProject)
  }

  function updateCreateBundle(newTitle: string, bundle?: Bundle): void {
    if (bundle) {
      updateBundle(bundle.id as string, newTitle)
    } else {
      createBundle(newTitle)
    }
  }

  function closeTextInputDialog(): void {
    setTextInputDialogState({
      ...textInputDialogState,
      open: false
    })
  }

  function handleProjectChangeBundle(project: ProjectDto): void {
    const projectBundles: Bundle[] = project.bundles ? project.bundles.map(bundleDtoToBundle) : []
    setSelectBundleDialogState({
      open: true,
      bundles: projectBundles,
      onClose: closeSelectBundleDialog,
      onSelect: (bundles: Bundle[]) => {
        updateProjectBundles(project, bundles, fetchBosatlas)
        closeSelectBundleDialog()
      }
    })
  }

  function handleComparisonChangeBundle(comparison: ProjectComparisonDto): void {
    const comparisonBundles: Bundle[] = comparison.bundles ? comparison.bundles.map(bundleDtoToBundle) : []
    setSelectBundleDialogState({
      open: true,
      bundles: comparisonBundles,
      onClose: closeSelectBundleDialog,
      onSelect: (bundles: Bundle[]) => {
        updateComparisonBundles(comparison, bundles, fetchBosatlas)
        closeSelectBundleDialog()
      }
    })
  }

  function closeSelectBundleDialog(): void {
    setSelectBundleDialogState({ ...selectBundleDialogState, open: false })
  }

  return (
    <PageContainer id="bosatlas">
      <BosatlasViewer
        bundles={bundles}
        projects={projects}
        comparisons={comparisons}
        sharedProjects={sharedProjects}
        sharedComparisons={sharedComparisons}
        textInputDialogState={textInputDialogState}
        selectBundleDialogState={selectBundleDialogState}
        startBundleEdit={startBundleEdit}
        onComparisonClick={onComparisonClick}
        onComparisonChangeBundle={handleComparisonChangeBundle}
        onComparisonRemove={removeComparison}
        onComparisonUpdate={handleComparisonUpdateRequest}
        onProjectClick={onProjectClick}
        onProjectChangeBundle={handleProjectChangeBundle}
        onProjectRemove={removeProject}
        onProjectUpdate={handleProjectUpdateRequest}
        onViewBundleContentClick={onBundleClick}
      />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    bundles: state.bosatlas.bundles,
    projects: state.bosatlas.projects,
    comparisons: state.bosatlas.projectComparisons,
    sharedProjects: state.bosatlas.sharedProjects,
    sharedComparisons: state.bosatlas.sharedComparisons
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasActions>) {
  return {
    fetchBosatlas: () => dispatch(fetchBosatlas()),
    removeComparison: (comparison: ProjectComparisonDto) => dispatch(bosatlasComparisonDelete(comparison)),
    removeProject: (project: ProjectDto) => dispatch(bosatlasProjectDelete(project)),
    updateComparison: (comparison: ProjectComparisonDto) => dispatch(bosatlasComparisonUpdate(comparison)),
    updateComparisonBundles: (comparison: ProjectComparisonDto, bundles: Bundle[], callback?: () => void) =>
      dispatch(bosatlasComparisonUpdateBundle(comparison, bundles, callback)),
    updateProject: (project: ProjectDto) => dispatch(bosatlasProjectUpdate(project)),
    updateProjectBundles: (project: ProjectDto, bundles: Bundle[], callback?: () => void) =>
      dispatch(bosatlasProjectUpdateBundle(project, bundles, callback)),
    createBundle: (name: string) => dispatch(bosatlasCreateBundle(name)),
    updateBundle: (id: string, name: string) => dispatch(bosatlasUpdateBundle(id, name)),
    searchBosatlas: (searchValue: string) => dispatch(bosatlasSearch(searchValue))
  }
}

export const BosatlasPage = connect(mapStateToProps, mapDispatchToProps)(BosatlasPageView)
